/**
 * Servizio di geocoding per ottenere coordinate da indirizzi
 * Supporta Google Maps (se API key disponibile) e Nominatim (OpenStreetMap) come fallback
 */

class GeocodingService {
  constructor() {
    this.googleApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || null
    this.nominatimBaseUrl = 'https://nominatim.openstreetmap.org/search'
    this.googleBaseUrl = 'https://maps.googleapis.com/maps/api/geocode/json'
  }

  /**
   * Geocodifica un indirizzo usando Google Maps (se disponibile) o Nominatim
   * @param {string} address - Indirizzo da geocodificare
   * @returns {Promise<{lat: number, lon: number, display_name: string}>}
   */
  async geocode(address) {
    if (!address || address.trim() === '') {
      throw new Error('Indirizzo non valido')
    }

    // Prova prima con Google Maps se la chiave è disponibile
    if (this.googleApiKey) {
      try {
        return await this.geocodeWithGoogle(address)
      } catch (error) {
        console.warn('Google Maps geocoding fallito, provo con Nominatim:', error.message)
      }
    }

    // Fallback su Nominatim
    return await this.geocodeWithNominatim(address)
  }

  /**
   * Geocodifica con Google Maps
   * @private
   */
  async geocodeWithGoogle(address) {
    const params = new URLSearchParams({
      address: address,
      key: this.googleApiKey
    })

    const response = await fetch(`${this.googleBaseUrl}?${params}`)

    if (!response.ok) {
      throw new Error('Errore nella richiesta a Google Maps')
    }

    const data = await response.json()

    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error(data.status === 'ZERO_RESULTS' ? 'Indirizzo non trovato' : `Google Maps error: ${data.status}`)
    }

    const result = data.results[0]
    return {
      lat: result.geometry.location.lat,
      lon: result.geometry.location.lng,
      display_name: result.formatted_address
    }
  }

  /**
   * Geocodifica con Nominatim (OpenStreetMap)
   * @private
   */
  async geocodeWithNominatim(address) {
    const params = new URLSearchParams({
      q: address,
      format: 'json',
      limit: 1,
      addressdetails: 1
    })

    const response = await fetch(`${this.nominatimBaseUrl}?${params}`, {
      headers: {
        'User-Agent': 'Poderi-App/1.0'
      }
    })

    if (!response.ok) {
      throw new Error('Errore nella richiesta a Nominatim')
    }

    const data = await response.json()

    if (!data || data.length === 0) {
      throw new Error('Indirizzo non trovato')
    }

    const result = data[0]
    return {
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon),
      display_name: result.display_name
    }
  }

  /**
   * Reverse geocoding: ottiene l'indirizzo da coordinate
   * @param {number} lat - Latitudine
   * @param {number} lon - Longitudine
   * @returns {Promise<string>}
   */
  async reverseGeocode(lat, lon) {
    // Prova prima con Google Maps se disponibile
    if (this.googleApiKey) {
      try {
        return await this.reverseGeocodeWithGoogle(lat, lon)
      } catch (error) {
        console.warn('Google Maps reverse geocoding fallito, provo con Nominatim:', error.message)
      }
    }

    // Fallback su Nominatim
    return await this.reverseGeocodeWithNominatim(lat, lon)
  }

  /**
   * Reverse geocoding con Google Maps
   * @private
   */
  async reverseGeocodeWithGoogle(lat, lon) {
    const params = new URLSearchParams({
      latlng: `${lat},${lon}`,
      key: this.googleApiKey
    })

    const response = await fetch(`${this.googleBaseUrl}?${params}`)

    if (!response.ok) {
      throw new Error('Errore nel reverse geocoding Google Maps')
    }

    const data = await response.json()

    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error(`Google Maps reverse geocoding error: ${data.status}`)
    }

    return data.results[0].formatted_address
  }

  /**
   * Reverse geocoding con Nominatim
   * @private
   */
  async reverseGeocodeWithNominatim(lat, lon) {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lon.toString(),
      format: 'json'
    })

    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`, {
      headers: {
        'User-Agent': 'Poderi-App/1.0'
      }
    })

    if (!response.ok) {
      throw new Error('Errore nel reverse geocoding Nominatim')
    }

    const data = await response.json()
    return data.display_name || ''
  }
}

export default new GeocodingService()
